import { IIdLabel } from "@mctypes/global.types";
import { Gantt, IZoomConfig } from "@svar-ui/react-gantt";
import "@svar-ui/react-gantt/all.css";
import React, { useEffect, useMemo, useRef } from "react";
import { useSelector } from "react-redux";
import { Willow } from "wx-react-gantt";
import { selectIssueBoard } from "../../../issueSelectors";
import { ISvarGantItem, SvarGanttColumn } from "../issueGantSelectors";
import "./SvarGantt.css";
// Willow styling broken in @svar-ui version, using previous wx-react-gantt
interface TaskDates {
  plannedStart: string;
  due: string;
}

interface GanttWrapperProps {
  start: Date | string;
  end: Date | string;
  zoom: IZoomConfig;
  columns: SvarGanttColumn[];
  gantItems: ISvarGantItem[];
  taskTypes: IIdLabel[];
  lengthUnit: string;
  onUpdateTask: (id: string, dates: TaskDates) => void;
  onReorderTasks?: (payload: { movedId: string; parentId?: string; newOrder: string[] }) => void;
}

const GanttWrapper: React.FC<GanttWrapperProps> = ({
  start,
  end,
  zoom,
  columns,
  gantItems,
  taskTypes,
  lengthUnit,
  onUpdateTask,
  onReorderTasks,
}) => {
  const apiRef = useRef<any>(null);
  const issueBoard = useSelector(selectIssueBoard);

  const lastSentKeyRef = useRef<Map<string, { key: string; ts: number }>>(new Map());
  const lastSentTsRef = useRef<Map<string, number>>(new Map());
  const globalWindowRef = useRef<{ start: number; count: number }>({ start: 0, count: 0 });

  const IDEMPOTENCY_WINDOW_MS = 1500;
  const PER_TASK_THROTTLE_MS = 400;
  const GLOBAL_WINDOW_MS = 3000;
  const GLOBAL_MAX_EVENTS = 20;
  const attachedRef = useRef(false);

  const toISO = (d: any) => new Date(d).toISOString();
  const makeKey = (id: string, startISO: string, endISO: string) => `${id}|${startISO}|${endISO}`;

  const allowGlobal = (): boolean => {
    const now = Date.now();
    const win = globalWindowRef.current;
    if (now - win.start > GLOBAL_WINDOW_MS) {
      win.start = now;
      win.count = 0;
    }
    win.count += 1;
    return win.count <= GLOBAL_MAX_EVENTS;
  };
  const allowPerTask = (taskId: string): boolean => {
    const now = Date.now();
    const last = lastSentTsRef.current.get(taskId) ?? 0;
    if (now - last < PER_TASK_THROTTLE_MS) return false;
    lastSentTsRef.current.set(taskId, now);
    return true;
  };

  const allowIdempotent = (taskId: string, startISO: string, endISO: string): boolean => {
    const now = Date.now();
    const key = makeKey(taskId, startISO, endISO);
    const prev = lastSentKeyRef.current.get(taskId);
    if (prev && prev.key === key && now - prev.ts < IDEMPOTENCY_WINDOW_MS) return false;
    lastSentKeyRef.current.set(taskId, { key, ts: now });
    return true;
  };

  const handleUpdate = (event: any) => {
    const task = event?.task;
    if (!task || event?.inProgress === true) return;

    const { id, start: s, end: e } = task;
    if (!id || s == null || e == null) return;
    if (s === e) return; // no actual change

    const startISO = toISO(s);
    const endISO = toISO(e);

    if (!allowGlobal()) return;
    if (!allowPerTask(id)) return;
    if (!allowIdempotent(id, startISO, endISO)) return;

    onUpdateTask(id, { plannedStart: startISO, due: endISO });
  };

  const gantItemsRef = useRef<ISvarGantItem[]>([]);
  useEffect(() => {
    gantItemsRef.current = gantItems;
  }, [gantItems]);

  const openedIdsRef = useRef<Set<string>>(new Set());

  const lastReorderRef = useRef<string>("");

  const handleReorder = (event: any) => {
    if (!onReorderTasks) return;
    if (event?.inProgress === true) return;

    const parentId = event?.source ? String(event.source) : null;
    const movedId = event?.id ? String(event.id) : null;
    const targetId = event?.target ? String(event.target) : null;
    const mode = event?.mode; // "before" | "after" | "child"

    if (!parentId || !movedId) return;

    // only handle sibling reorder (ignore "child" unless you support nesting)
    if (mode !== "before" && mode !== "after") return;

    // ✅ siblings under this parent (issue) – use your current props `gantItems`
    const siblings = gantItemsRef.current
      .filter((t: any) => String(t.parent ?? "") === parentId)
      .map((t: any) => String(t.id));
    if (!siblings.includes(movedId)) return;

    // If dropped "on top" (target === movedId), nothing to do
    const withoutMoved = siblings.filter((id) => id !== movedId);

    let insertIdx: number;
    // If target is "self" or missing, interpret as move to top/bottom of the group
    if (!targetId || targetId === movedId) {
      insertIdx = mode === "before" ? 0 : withoutMoved.length;
    } else {
      const targetIdx = withoutMoved.indexOf(targetId);
      if (targetIdx === -1) return;
      insertIdx = mode === "before" ? targetIdx : targetIdx + 1;
    }

    const newOrder = [...withoutMoved.slice(0, insertIdx), movedId, ...withoutMoved.slice(insertIdx)];

    // dedupe identical payloads
    const key = `${parentId}|${newOrder.join(",")}`;
    if (lastReorderRef.current === key) return;
    lastReorderRef.current = key;

    onReorderTasks({ movedId, parentId, newOrder });
  };

  const attachOnce = (api: any) => {
    if (!api || attachedRef.current) return;
    attachedRef.current = true;

    apiRef.current = api;

    api.on("move-task", (e: any) => {
      handleReorder(e);
    });

    api.on("update-task", handleUpdate);

    api.on("open-task", (event: { id: string | number; mode: boolean }) => {
      const key = String(event.id);
      const store = openedIdsRef.current;
      if (event.mode === true) store.add(key);
      else store.delete(key);
    });

    api.intercept("show-editor", (data: any) => {
      window.location.href = `${window.location.href.split("?")[0]}?id=${data?.id}`;
      return false;
    });
  };

  // When tasks are refreshed, re-apply the stored collapsed state
  // so that branches the user had opened stay opened.
  useEffect(() => {
    const api = apiRef.current;
    if (!api) return;

    const store = openedIdsRef.current;
    if (!store.size) return;

    const existingIds = new Set((gantItems || []).map((t: any) => String(t.id)));

    store?.forEach((id) => {
      if (!existingIds.has(id)) {
        // task no longer present – clean up
        store.delete(id);
        return;
      }

      api.exec("open-task", { id, mode: true }); //crashes if not collapsed and filter toggled on "fullført"?
    });
  }, [gantItems]);

  const vacationRanges = useMemo(
    () =>
      issueBoard?.ganttVacationSettings?.map((v) => ({
        start: new Date(v.startDate),
        end: new Date(v.endDate),
      })) || [],
    [issueBoard],
  );

  const normalize = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate());

  const isVacationDay = (date: Date) => {
    const d = normalize(date);
    return vacationRanges.some((v) => d >= normalize(v.start) && d <= normalize(v.end));
  };

  const highlightTime = (date: Date, unit: string) => {
    if (unit === "day") {
      if (isVacationDay(date)) return "wx-weekend";
    } else if (unit === "week" && zoom?.level === 2) {
      const start = new Date(date);
      const end = new Date(date);
      end.setDate(end.getDate() + 6); // whole week

      const isWeekVacation = vacationRanges.some(
        (v) => normalize(v.start) <= normalize(end) && normalize(v.end) >= normalize(start),
      );
      return isWeekVacation ? "wx-weekend" : "";
    }

    return "";
  };

  return (
    <Willow>
      <Gantt
        start={start as Date}
        end={end as Date}
        tasks={gantItems.length ? gantItems : []}
        zoom={zoom}
        columns={columns}
        taskTypes={taskTypes}
        lengthUnit={lengthUnit}
        highlightTime={highlightTime}
        init={attachOnce}
      />
    </Willow>
  );
};

export default GanttWrapper;
